// ==========================
// Version 2 - wallet/walletLinking.js
// - Links a wallet address to the signed-in BWK user
// - Reserves each wallet address globally in wallet_links/{address}
// - Provides shared wallet-address normalization and match helpers
// ==========================
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export const WALLET_LINK_SIGNATURE_VERSION = "bwk-wallet-link-v1";

export function normalizeWalletAddress(address) {
  const value = String(address || "").trim();
  return /^0x[a-fA-F0-9]{40}$/.test(value) ? value.toLowerCase() : "";
}

export function isValidWalletAddress(address) {
  return !!normalizeWalletAddress(address);
}

export function walletMatchesLinkedAddress(activeAddress, linkedWallet) {
  const active = normalizeWalletAddress(activeAddress);
  const linked = normalizeWalletAddress(linkedWallet?.primaryAddress);
  return !!active && !!linked && active === linked;
}

export function buildWalletLinkMessage({ uid, address, issuedAt = new Date().toISOString() }) {
  const normalizedAddress = normalizeWalletAddress(address);
  if (!uid) throw new Error("Missing BWK account id.");
  if (!normalizedAddress) throw new Error("Invalid wallet address.");

  return [
    "Blessed with a Kiss wallet link",
    "",
    `Account: ${uid}`,
    `Wallet: ${normalizedAddress}`,
    `Issued at: ${issuedAt}`,
    `Version: ${WALLET_LINK_SIGNATURE_VERSION}`,
    "",
    "Only sign this message if you want to link this wallet to your BWK account.",
  ].join("\n");
}

export async function signWalletLinkMessage({ address, message }) {
  const normalizedAddress = normalizeWalletAddress(address);
  const eth = typeof window !== "undefined" ? window.ethereum : null;

  if (!normalizedAddress) throw new Error("Invalid wallet address.");
  if (!eth?.request) throw new Error("No wallet provider found.");

  return eth.request({
    method: "personal_sign",
    params: [message, normalizedAddress],
  });
}

export async function saveLinkedWallet({ uid, address, chainId, walletId, signature, message }) {
  const normalizedAddress = normalizeWalletAddress(address);
  if (!uid) throw new Error("Missing BWK account id.");
  if (!normalizedAddress) throw new Error("Invalid wallet address.");
  if (!signature) throw new Error("Wallet signature is required.");

  await runTransaction(db, async (tx) => {
    const userRef = doc(db, "users", uid);
    const linkRef = doc(db, "wallet_links", normalizedAddress);
    const [userSnap, linkSnap] = await Promise.all([
      tx.get(userRef),
      tx.get(linkRef),
    ]);

    const currentLinkedAddress = normalizeWalletAddress(userSnap.data()?.wallet?.primaryAddress);

    if (linkSnap.exists()) {
      const ownerUid = linkSnap.data()?.ownerUid;
      if (ownerUid && ownerUid !== uid) {
        throw new Error("This wallet is already linked to another BWK account.");
      }
    }

    if (currentLinkedAddress && currentLinkedAddress !== normalizedAddress) {
      const oldLinkRef = doc(db, "wallet_links", currentLinkedAddress);
      const oldLinkSnap = await tx.get(oldLinkRef);
      if (oldLinkSnap.exists() && oldLinkSnap.data()?.ownerUid === uid) {
        tx.delete(oldLinkRef);
      }
    }

    const walletRecord = {
      primaryAddress: normalizedAddress,
      chainId: Number(chainId) || null,
      provider: walletId || "thirdweb",
      signature,
      message,
      signatureVersion: WALLET_LINK_SIGNATURE_VERSION,
      linkedAt: serverTimestamp(),
      lastConfirmedAt: serverTimestamp(),
    };

    if (linkSnap.exists()) {
      tx.update(linkRef, {
        provider: walletRecord.provider,
        chainId: walletRecord.chainId,
        signatureVersion: WALLET_LINK_SIGNATURE_VERSION,
        lastConfirmedAt: serverTimestamp(),
      });
    } else {
      tx.set(linkRef, {
        primaryAddress: normalizedAddress,
        ownerUid: uid,
        provider: walletRecord.provider,
        chainId: walletRecord.chainId,
        signatureVersion: WALLET_LINK_SIGNATURE_VERSION,
        linkedAt: serverTimestamp(),
        lastConfirmedAt: serverTimestamp(),
      });
    }

    tx.set(userRef, { wallet: walletRecord }, { merge: true });
  });

  return normalizedAddress;
}

export async function unlinkWallet({ uid }) {
  if (!uid) throw new Error("Missing BWK account id.");
  await runTransaction(db, async (tx) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await tx.get(userRef);
    const currentLinkedAddress = normalizeWalletAddress(userSnap.data()?.wallet?.primaryAddress);

    if (currentLinkedAddress) {
      const linkRef = doc(db, "wallet_links", currentLinkedAddress);
      const linkSnap = await tx.get(linkRef);
      if (linkSnap.exists() && linkSnap.data()?.ownerUid === uid) {
        tx.delete(linkRef);
      }
    }

    tx.set(userRef, { wallet: null }, { merge: true });
  });
}

// ==========================
// End Version 2 - wallet/walletLinking.js
// ==========================
